import type { GameState } from "@dailywars/core/engine";
import { mapThemeConfig } from "@dailywars/core/render/mapThemes";

import {
  ATTACKABLE_TILE_FILL,
  HOVER_TILE_STROKE,
  TILE_GRID_BORDER_STROKE,
  PATH_TILE_FILL,
  TILE_SIZE,
  type CanvasViewState,
} from "./boardConstants";

function tileRect({ x, y }: { x: number; y: number }): {
  left: number;
  top: number;
  size: number;
} {
  return {
    left: x * TILE_SIZE,
    top: y * TILE_SIZE,
    size: TILE_SIZE,
  };
}

function drawReachableHatch({
  ctx,
  x,
  y,
  fill,
  hatch,
}: {
  ctx: CanvasRenderingContext2D;
  x: number;
  y: number;
  fill: string;
  hatch: string;
}): void {
  const { left, top, size } = tileRect({ x, y });
  ctx.fillStyle = fill;
  ctx.fillRect(left, top, size, size);

  ctx.save();
  ctx.beginPath();
  ctx.rect(left, top, size, size);
  ctx.clip();
  ctx.strokeStyle = hatch;
  ctx.lineWidth = 2;
  for (let offset = -size; offset < size * 2; offset += 8) {
    ctx.beginPath();
    ctx.moveTo(left + offset, top);
    ctx.lineTo(left + offset + size, top + size);
    ctx.stroke();
  }
  ctx.restore();
}

function drawHoverHighlight({
  ctx,
  x,
  y,
  key,
  hoveredTileKey,
}: {
  ctx: CanvasRenderingContext2D;
  x: number;
  y: number;
  key: string;
  hoveredTileKey: string | null;
}): void {
  if (!hoveredTileKey || hoveredTileKey !== key) return;
  const { left, top, size } = tileRect({ x, y });
  ctx.strokeStyle = HOVER_TILE_STROKE;
  ctx.lineWidth = 2;
  ctx.strokeRect(left + 1.5, top + 1.5, size - 3, size - 3);
}

function drawTileGridBorder({
  ctx,
  x,
  y,
}: {
  ctx: CanvasRenderingContext2D;
  x: number;
  y: number;
}): void {
  const { left, top, size } = tileRect({ x, y });
  ctx.strokeStyle = TILE_GRID_BORDER_STROKE;
  ctx.lineWidth = 1;
  ctx.strokeRect(left, top, size, size);
}

function drawTileOverlaysAtCell({
  ctx,
  x,
  y,
  key,
  viewState,
  reachable,
  path,
  attackableTiles,
  reachableTileFill,
  reachableTileHatch,
}: {
  ctx: CanvasRenderingContext2D;
  x: number;
  y: number;
  key: string;
  viewState: CanvasViewState;
  reachable: Set<string>;
  path: Set<string>;
  attackableTiles: Set<string>;
  reachableTileFill: string;
  reachableTileHatch: string;
}): void {
  if (reachable.has(key)) {
    drawReachableHatch({
      ctx,
      x,
      y,
      fill: reachableTileFill,
      hatch: reachableTileHatch,
    });
  }
  if (path.has(key)) {
    const { left, top, size } = tileRect({ x, y });
    ctx.fillStyle = PATH_TILE_FILL;
    ctx.fillRect(left, top, size, size);
  }
  if (attackableTiles.has(key)) {
    const { left, top, size } = tileRect({ x, y });
    ctx.fillStyle = ATTACKABLE_TILE_FILL;
    ctx.fillRect(left, top, size, size);
  }
  drawTileGridBorder({ ctx, x, y });
  drawHoverHighlight({ ctx, x, y, key, hoveredTileKey: viewState.hoveredTileKey });
}

export function drawTileOverlaysLayer({
  ctx,
  state,
  viewState,
  usableReachable: reachable,
  path,
  attackableTiles,
}: {
  ctx: CanvasRenderingContext2D;
  state: GameState;
  viewState: CanvasViewState;
  usableReachable: Set<string>;
  path: Set<string>;
  attackableTiles: Set<string>;
}): void {
  const { reachableTileFill, reachableTileHatch } = mapThemeConfig(state.theme);

  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      const key = `${x},${y}`;
      drawTileOverlaysAtCell({
        ctx,
        x,
        y,
        key,
        viewState,
        reachable,
        path,
        attackableTiles,
        reachableTileFill,
        reachableTileHatch,
      });
    }
  }
}
